import { memo, useCallback, useMemo } from 'react'
import { Security } from '../../proto/generated/security'
import { Box, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import { useStyles } from './styles'
import { getSecurityKey } from '../../util/security'
import { toLocaleStringIn } from '../../util/general'
import { useDispatch } from 'react-redux'
import { setQuickOrderData } from '../../reducer/orders.reducer'
import { Broker_OrderType, OrderType, Product, TransactionType } from '../../proto/generated/order'
import { isTradeableSecurity } from '../../util/cashInstrument'
import {
    useHasOrderBook,
    useOrderBookQuoteCount,
    useOrderBookQuoteField,
    useOrderBookSideMaxQuantity,
    useOrderBookSideTotal,
} from '../../hooks/useSecurityMarketTicks'
import store from '../../store'

export type MarketDepthProps = {
    security: Security
    tradeable?: boolean
}

const EMPTY_DEPTH_ROWS = 5

type DepthSide = 'bid' | 'ask'

const BID_GRADIENT = 'rgba(65, 132, 243, 0.1)'
const ASK_GRADIENT = 'rgba(255, 87, 34, 0.1)'

const isMarketQuote = (price?: number | null, quantity?: number | null) =>
    (price == null || price === 0) && (quantity ?? 0) > 0

type LiveFieldProps = {
    securityKey: string
    side: DepthSide
    index: number
}

const DepthPriceValue = memo(function DepthPriceValue({
    securityKey,
    side,
    index,
    priceMultiplier,
    isMarketPrice,
    showMarketLabel,
}: LiveFieldProps & {
    priceMultiplier: number
    isMarketPrice: boolean
    showMarketLabel: boolean
}) {
    const price = useOrderBookQuoteField(securityKey, side, index, 'price')

    if (isMarketPrice) {
        if (!showMarketLabel) return null
        return (
            <Typography component="span" variant="caption" color="text.secondary">
                Market
            </Typography>
        )
    }

    return (
        <span className="font-feature-num">
            {price != null ? toLocaleStringIn(price / priceMultiplier) : '0.00'}
        </span>
    )
})

const DepthOrdersValue = memo(function DepthOrdersValue({ securityKey, side, index }: LiveFieldProps) {
    const numberOfOrders = useOrderBookQuoteField(securityKey, side, index, 'numberOfOrders')
    return <span className="font-feature-num">{numberOfOrders ?? 0}</span>
})

const DepthQuantityValue = memo(function DepthQuantityValue({
    securityKey,
    side,
    index,
}: LiveFieldProps) {
    const quantity = useOrderBookQuoteField(securityKey, side, index, 'quantity')
    const maxQuantity = useOrderBookSideMaxQuantity(securityKey, side)
    const shadowWidth = maxQuantity > 0 ? ((quantity ?? 0) / maxQuantity) * 100 : 0
    const gradientColor = side === 'bid' ? BID_GRADIENT : ASK_GRADIENT

    return (
        <div style={{ paddingLeft: 8, height: '100%' }}>
            <div
                style={{
                    height: '100%',
                    background: `linear-gradient(to left, ${gradientColor} ${shadowWidth}%, transparent ${shadowWidth}%)`,
                }}
            >
                <span className="font-feature-num">{quantity ?? 0}</span>
            </div>
        </div>
    )
})

const DepthTotalValue = memo(function DepthTotalValue({
    securityKey,
    side,
}: {
    securityKey: string
    side: DepthSide
}) {
    const total = useOrderBookSideTotal(securityKey, side)
    return <span className="font-feature-num">{total}</span>
})

type DepthRowProps = {
    securityKey: string
    side: DepthSide
    index: number
    priceMultiplier: number
    tradeable: boolean
    onRowClick: (type: DepthSide, price: number) => void
}

const MarketDepthRow = memo(function MarketDepthRow({
    securityKey,
    side,
    index,
    priceMultiplier,
    tradeable,
    onRowClick,
}: DepthRowProps) {
    const { classes, cx } = useStyles()
    const sideClass = side === 'bid' ? classes.bidCell : classes.askCell

    const bidPrice = useOrderBookQuoteField(securityKey, 'bid', index, 'price')
    const bidQty = useOrderBookQuoteField(securityKey, 'bid', index, 'quantity')
    const askPrice = useOrderBookQuoteField(securityKey, 'ask', index, 'price')
    const askQty = useOrderBookQuoteField(securityKey, 'ask', index, 'quantity')
    const bidMarket = isMarketQuote(bidPrice, bidQty)
    const askMarket = isMarketQuote(askPrice, askQty)
    const isMarketPrice = side === 'bid' ? bidMarket : askMarket
    // Single "Market" label: prefer bid when both sides are market.
    const showMarketLabel = side === 'bid' ? bidMarket : askMarket && !bidMarket

    const handleClick = useCallback(() => {
        if (!tradeable) return
        const book = store.getState().securityStore.orderBooks[securityKey]
        const quotes = side === 'bid' ? book?.bids : book?.asks
        onRowClick(side, quotes?.[index]?.price ?? 0)
    }, [securityKey, side, index, tradeable, onRowClick])

    return (
        <TableRow
            className={cx(classes.tableRow, { [classes.tradeable]: tradeable })}
            onClick={tradeable ? handleClick : undefined}
        >
            <TableCell className={cx(classes.rowCell, showMarketLabel ? undefined : !isMarketPrice && sideClass)}>
                <DepthPriceValue
                    securityKey={securityKey}
                    side={side}
                    index={index}
                    priceMultiplier={priceMultiplier}
                    isMarketPrice={isMarketPrice}
                    showMarketLabel={showMarketLabel}
                />
            </TableCell>
            <TableCell className={cx(classes.rowCell, sideClass)}>
                <DepthOrdersValue securityKey={securityKey} side={side} index={index} />
            </TableCell>
            <TableCell className={cx(classes.rowCell, sideClass)}>
                <DepthQuantityValue securityKey={securityKey} side={side} index={index} />
            </TableCell>
        </TableRow>
    )
})

const MarketDepthEmptyRow = memo(function MarketDepthEmptyRow({
    side,
    tradeable,
    onRowClick,
}: {
    side: DepthSide
    tradeable: boolean
    onRowClick: (type: DepthSide, price: number) => void
}) {
    const { classes, cx } = useStyles()
    const sideClass = side === 'bid' ? classes.bidCell : classes.askCell

    return (
        <TableRow
            className={cx(classes.tableRow, { [classes.tradeable]: tradeable })}
            onClick={tradeable ? () => onRowClick(side, 0) : undefined}
        >
            <TableCell className={cx(classes.rowCell, sideClass)}>
                <span className="font-feature-num">0.00</span>
            </TableCell>
            <TableCell className={cx(classes.rowCell, sideClass)}>
                <span className="font-feature-num">0</span>
            </TableCell>
            <TableCell className={cx(classes.rowCell, sideClass)}>
                <span className="font-feature-num">0</span>
            </TableCell>
        </TableRow>
    )
})

const MarketDepthTotalRow = memo(function MarketDepthTotalRow({
    securityKey,
    side,
}: {
    securityKey: string
    side: DepthSide
}) {
    const { classes, cx } = useStyles()
    const sideClass = side === 'bid' ? classes.bidCell : classes.askCell
    const buyQty = useOrderBookSideTotal(securityKey, 'bid')
    const sellQty = useOrderBookSideTotal(securityKey, 'ask')
    const combined = buyQty + sellQty
    const sideQty = side === 'bid' ? buyQty : sellQty
    const pct = combined > 0 ? Math.round((sideQty / combined) * 100) : 0

    return (
        <TableRow className={classes.tableRow}>
            <TableCell className={cx(classes.rowCell, classes.totalLabelCell)}>Total</TableCell>
            <TableCell className={cx(classes.rowCell, sideClass)} />
            <TableCell className={cx(classes.rowCell, sideClass)}>
                {combined > 0 && (
                    <span className={cx(classes.totalPct, 'font-feature-num')}>{pct}%</span>
                )}
                <DepthTotalValue securityKey={securityKey} side={side} />
            </TableCell>
        </TableRow>
    )
})

type MarketDepthSideProps = {
    securityKey: string
    side: DepthSide
    priceMultiplier: number
    tradeable: boolean
    onRowClick: (type: DepthSide, price: number) => void
}

const MarketDepthSide = memo(function MarketDepthSide({
    securityKey,
    side,
    priceMultiplier,
    tradeable,
    onRowClick,
}: MarketDepthSideProps) {
    const { classes } = useStyles()
    const quoteCount = useOrderBookQuoteCount(securityKey, side)
    const label = side === 'bid' ? 'BIDS' : 'OFFERS'

    const rows =
        quoteCount > 0
            ? Array.from({ length: quoteCount }, (_, index) => (
                  <MarketDepthRow
                      key={`${side}-${index}`}
                      securityKey={securityKey}
                      side={side}
                      index={index}
                      priceMultiplier={priceMultiplier}
                      tradeable={tradeable}
                      onRowClick={onRowClick}
                  />
              ))
            : Array.from({ length: EMPTY_DEPTH_ROWS }, (_, index) => (
                  <MarketDepthEmptyRow
                      key={`empty-${side}-${index}`}
                      side={side}
                      tradeable={tradeable}
                      onRowClick={onRowClick}
                  />
              ))

    return (
        <Box width="50%">
            <Table className={classes.table}>
                <TableHead>
                    <TableRow>
                        <TableCell className={classes.headerCell}>
                            <Typography variant="caption">{label}</Typography>
                        </TableCell>
                        <TableCell className={classes.headerCell}>
                            <Typography variant="caption">ORDERS</Typography>
                        </TableCell>
                        <TableCell className={classes.headerCell}>
                            <Typography variant="caption">QTY</Typography>
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows}
                    <MarketDepthTotalRow securityKey={securityKey} side={side} />
                </TableBody>
            </Table>
        </Box>
    )
})

const MarketDepth: React.FC<MarketDepthProps> = ({ security, tradeable }) => {
    const dispatch = useDispatch()
    const securityKey = useMemo(
        () => (security ? getSecurityKey(security.exchange, security.code) : ''),
        [security]
    )
    const hasOrderBook = useHasOrderBook(securityKey)
    const priceMultiplier = security?.priceMultiplier ?? 100
    const isTradeable = tradeable ?? isTradeableSecurity(security)

    const onRowClick = useCallback(
        (type: DepthSide, price: number) => {
            if (!isTradeable) return
            dispatch(
                setQuickOrderData({
                    openDialog: true,
                    mode: 'regular',
                    type: 'trade',
                    isSquareOffOrder: false,
                    order: {
                        transactionType: type === 'bid' ? TransactionType.SELL : TransactionType.BUY,
                        security: security,
                        editOrder: undefined,
                        brokerOrderType: Broker_OrderType.LIMIT_ORDER,
                        orderType: OrderType.NORMAL,
                        product: Product.CNC,
                        price: price,
                        quantity: 1 * (security.lotSize ?? 1),
                        maxQuantity: 0,
                    },
                })
            )
        },
        [dispatch, security, isTradeable]
    )

    if (!hasOrderBook) return null

    return (
        <Box display="flex" flexDirection="row" gap={1} justifyContent="space-between" width="100%">
            <MarketDepthSide
                securityKey={securityKey}
                side="bid"
                priceMultiplier={priceMultiplier}
                tradeable={isTradeable}
                onRowClick={onRowClick}
            />
            <MarketDepthSide
                securityKey={securityKey}
                side="ask"
                priceMultiplier={priceMultiplier}
                tradeable={isTradeable}
                onRowClick={onRowClick}
            />
        </Box>
    )
}

export default memo(MarketDepth)
